import { useEffect, useState } from "react";
import { Loader2, UserPlus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../adminApi";
import { useAdminAuth } from "../AdminAuthContext";

const FIELD = "w-full rounded-lg border border-[#E5E7EB] px-4 py-2.5 text-sm focus:outline-none focus:border-[#1E88E5] transition";

export default function AdminAdmins() {
  const { user } = useAdminAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => adminApi.get("/admin/users").then(({ data }) => setAdmins(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await adminApi.post("/admin/users", form);
      toast.success("Admin created");
      setForm({ name: "", email: "", password: "", role: "admin" });
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this admin user?")) return;
    try {
      await adminApi.delete(`/admin/users/${id}`);
      toast.success("Admin removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-admins">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#0A2342]">Admin Users</h1>
        <p className="text-sm text-[#4B5563] mt-1">Manage who can access this dashboard. (Super Admin only)</p>
      </div>

      <form onSubmit={create} className="bg-white rounded-xl border border-[#E5E7EB] p-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="create-admin-form">
        <div>
          <label className="block text-sm font-semibold text-[#0A2342] mb-1.5">Name</label>
          <input data-testid="admin-name" className={FIELD} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#0A2342] mb-1.5">Email</label>
          <input data-testid="admin-email" type="email" className={FIELD} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#0A2342] mb-1.5">Password</label>
          <input data-testid="admin-password" type="password" className={FIELD} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Min 8 characters" required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#0A2342] mb-1.5">Role</label>
          <select data-testid="admin-role" className={FIELD} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        {error && <p className="sm:col-span-2 text-sm text-red-700">{error}</p>}
        <div className="sm:col-span-2">
          <button data-testid="create-admin-submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#0A2342] text-white px-6 py-3 font-semibold hover:bg-[#1E88E5] transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <UserPlus size={18} />} Create Admin
          </button>
        </div>
      </form>

      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-[#4B5563]"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : (
          <ul className="divide-y divide-[#F4F6F8]">
            {admins.map((a) => (
              <li key={a.id} className="flex items-center gap-4 px-5 py-4" data-testid={`admin-row-${a.id}`}>
                <span className="h-10 w-10 rounded-lg bg-[#F4F6F8] flex items-center justify-center text-[#1E88E5]"><ShieldCheck size={18} /></span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#0A2342] truncate">{a.name} {a.id === user?.id && <span className="text-xs text-[#4B5563]">(you)</span>}</p>
                  <p className="text-sm text-[#4B5563] truncate">{a.email}</p>
                </div>
                <span className="text-xs rounded px-2 py-1 bg-[#F4F6F8] text-[#4B5563] uppercase tracking-wide">{a.role === "super_admin" ? "Super Admin" : "Admin"}</span>
                {a.id !== user?.id && (
                  <button onClick={() => del(a.id)} data-testid={`delete-admin-${a.id}`} className="p-2 text-[#4B5563] hover:text-red-600"><Trash2 size={18} /></button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
